import { useState } from 'react';

import { useAppDispatch, useAppSelector } from '@app/hooks';
import { useOpenYearMutation } from '@shared/api/catalogApi';
import { apiErrorMessage } from '@shared/api/errors';
import { BaseButton, BaseDialog, BaseSelect, useToast } from '@shared/components/base';
import { COMMON_TEXT } from '@shared/constants/text';
import { selectIsAdmin } from '@shared/session/sessionSlice';

import { SHELL_TEXT } from '../constants';
import { setProcessingYear } from '../uiSlice';
import { useProcessingYear } from '../useProcessingYear';

/** "Calendar year" switch, in My Profile and after sign-in: the year every page works in.
 *  Admins can open the next year, which rollover then fills (plan §8.7). `locked` is the
 *  masthead's read-only copy (changed in My Profile). `bare` drops the caption where the
 *  surrounding row already names it. `selectOpened` switches to a year as soon as it opens. */
export function YearPicker({
  locked = false,
  bare = false,
  selectOpened = false,
  lastUsed,
  fill = false,
}: {
  locked?: boolean;
  bare?: boolean;
  selectOpened?: boolean;
  /** Marks this year "Last used" in the list (the year-choice screen after sign-in). */
  lastUsed?: number;
  /** Fill the container's width (the year card after sign-in); the dropdown takes the rest. */
  fill?: boolean;
}) {
  const dispatch = useAppDispatch();
  const isAdmin = useAppSelector(selectIsAdmin);
  const toast = useToast();
  const { year, years } = useProcessingYear();
  const [openYear, { isLoading }] = useOpenYearMutation();
  const [confirming, setConfirming] = useState(false);
  const next = Math.max(year, ...years) + 1;

  // Newest first; type to search, so a long list of years stays quick.
  const options = years
    .toSorted((a, b) => b - a)
    .map((y) => ({
      value: String(y),
      label: String(y),
      ...(y === lastUsed ? { badge: SHELL_TEXT.yearPicker.lastUsed } : {}),
    }));

  const open = async () => {
    const res = await openYear({ year: next });
    if ('error' in res && res.error) {
      toast.error(apiErrorMessage(res.error));
      return;
    }
    toast.success(SHELL_TEXT.yearPicker.opened(next));
    if (selectOpened) dispatch(setProcessingYear(next));
    setConfirming(false);
  };

  return (
    <div
      className={fill ? 'flex w-full items-center gap-2' : 'flex items-center gap-2'}
      title={locked ? SHELL_TEXT.yearPicker.changeInSettings : undefined}
    >
      {!bare && (
        <span className="text-[10.5px] tracking-[0.13em] text-text-3">
          {SHELL_TEXT.yearPicker.calendarYear}
        </span>
      )}
      {/* One fixed width wherever the year is chosen (fits "2026 LAST USED"); the
          masthead's read-only copy stays compact. */}
      <div className={locked ? 'w-30' : fill ? 'min-w-0 flex-1' : 'w-48'}>
        <BaseSelect<string>
          id={locked ? 'processing-year-shown' : 'processing-year'}
          value={String(year)}
          options={options}
          onChange={(v) => {
            if (v) dispatch(setProcessingYear(Number(v)));
          }}
          aria-label={locked ? SHELL_TEXT.yearPicker.inUse : SHELL_TEXT.yearPicker.calendarYear}
          disabled={locked}
          searchable={!locked}
        />
      </div>
      {isAdmin && !locked && (
        <BaseButton
          variant="secondary"
          className="whitespace-nowrap"
          onClick={() => {
            setConfirming(true);
          }}
          title={SHELL_TEXT.yearPicker.openYearTitle(next)}
        >
          {SHELL_TEXT.yearPicker.openYear(next)}
        </BaseButton>
      )}
      <BaseDialog
        visible={confirming}
        onHide={() => {
          setConfirming(false);
        }}
        header={SHELL_TEXT.yearPicker.openYearTitle(next)}
        width="sm"
        footer={
          <>
            <BaseButton
              variant="secondary"
              onClick={() => {
                setConfirming(false);
              }}
            >
              {COMMON_TEXT.actions.cancel}
            </BaseButton>
            <BaseButton loading={isLoading} onClick={() => void open()}>
              {SHELL_TEXT.yearPicker.openYear(next)}
            </BaseButton>
          </>
        }
      >
        <p className="text-[13px] normal-case">{SHELL_TEXT.yearPicker.openYearMessage(next)}</p>
      </BaseDialog>
    </div>
  );
}
